import { ReactComponentOrElement, useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { Tooltip } from 'react-tooltip'

import { MODAL_NO_BLUR, ModalDismiss } from '@apps-common/config/modal'
import { modalFadeInEnterAnimation, modalFadeInLeaveAnimation } from '@apps-common/lib/modal-animations'

export const MODAL_BUTTON_ROUNDED_STYLES = 'flex items-center justify-center hover:opacity-70 disabled:opacity-30 bg-surface'

export const MODAL_BUTTON_ROUNDED_SM_STYLES = 'w-[30px] h-[30px] rounded-[15px] flex items-center justify-center bg-surface'

interface IProps {
  component: ReactComponentOrElement
  disabled?: boolean
  componentProps?: any
  cssClass?: string
  onClickCallback?: (e: any) => void
  confirmCallback?: (data: any) => void
  confirmCallbackAfterDismiss?: (data: any) => void
  cancelCallback?: () => void
  classes?: string
  children: ReactNode
  backdropDismiss?: boolean
  enterAnimation?: any
  leaveAnimation?: any
  tooltip?: {
    id: string
    message: string
    place?: any
  }
  noBlur?: boolean
  modalOpts?: any
}

export const ModalButton = ({
  component,
  disabled = false,
  componentProps,
  cssClass,
  onClickCallback,
  confirmCallback,
  confirmCallbackAfterDismiss,
  cancelCallback,
  classes = MODAL_BUTTON_ROUNDED_STYLES,
  children,
  backdropDismiss = false,
  enterAnimation = modalFadeInEnterAnimation,
  leaveAnimation = modalFadeInLeaveAnimation,
  tooltip,
  noBlur = false,
  modalOpts = {}
}: IProps) => {
  const [present, dismiss] = useIonModal(component, {
    componentProps,
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  const open = () => {
    present({
      ...modalOpts,
      cssClass: cssClass || noBlur ? MODAL_NO_BLUR.cssClass : '',
      enterAnimation,
      leaveAnimation,
      backdropDismiss,
      onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        if (ev.detail.role === ModalDismiss.Confirm && confirmCallback) {
          let { data } = ev.detail

          if (typeof ev.detail.data === 'string') {
            try {
              data = JSON.parse(ev.detail.data)
            } catch {
              // failed to parse data from modal
            }
          }
          confirmCallback(data)
        } else if (ev.detail.role === ModalDismiss.Cancel && cancelCallback) {
          cancelCallback()
        }
      },
      onDidDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        if (ev.detail.role === ModalDismiss.Confirm && confirmCallbackAfterDismiss) {
          let { data } = ev.detail

          if (typeof ev.detail.data === 'string') {
            try {
              data = JSON.parse(ev.detail.data)
            } catch {
              // failed to parse data from modal
            }
          }
          confirmCallbackAfterDismiss(data)
        } else if (ev.detail.role === ModalDismiss.Cancel && cancelCallback) {
          cancelCallback()
        }
      }
    })
  }

  return tooltip ? (
    <div data-tooltip-id={tooltip.id} data-tooltip-content={tooltip.message}>
      <button
        type='button'
        className={classNames(
          '!outline-none hover:opacity-80 disabled:opacity-30 bg-transparent',
          classes,
          disabled && 'cursor-not-allowed'
        )}
        disabled={disabled}
        onClick={(e) => {
          if (onClickCallback) {
            onClickCallback(e)
          }
          open()
        }}
      >
        {children}
      </button>
      <Tooltip id={tooltip.id} place={tooltip.place} className='hidden sm:inline-block !bg-surface !text-text !drop-shadow-sm z-[60]' />
    </div>
  ) : (
    <button
      type='button'
      className={classNames('!outline-none hover:opacity-95 disabled:opacity-30 bg-transparent', classes, disabled && 'cursor-not-allowed')}
      disabled={disabled}
      onClick={(e) => {
        if (onClickCallback) {
          onClickCallback(e)
        }
        open()
      }}
    >
      {children}
    </button>
  )
}
